import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { MentionTimelinePoint } from '@/api/queriesService';

interface MentionsChartProps {
  data: MentionTimelinePoint[];
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const date = new Date(label);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    return (
      <div className="bg-white border border-[rgba(2,2,3,0.1)] rounded p-3 shadow-lg">
        <p className="font-mono text-xs text-[#020203] opacity-60 mb-2">{formattedDate}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="font-mono text-xs uppercase tracking-wide">
              {entry.name}:
            </span>
            <span className="font-semibold">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const MentionsChart: React.FC<MentionsChartProps> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart
        data={data}
        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,2,3,0.06)" />
        <XAxis
          dataKey="date"
          tickFormatter={formatDate}
          tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
          tickLine={false}
          axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
          tickLine={false}
          axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: 16 }}
          iconType="circle"
          iconSize={8}
          formatter={(value: string) => (
            <span className="font-mono text-xs uppercase tracking-wide text-[#020203] opacity-70">
              {value}
            </span>
          )}
        />
        <Line
          type="monotone"
          dataKey="mentions"
          name="Mentions"
          stroke="#00a944"
          strokeWidth={2}
          dot={{ fill: '#00a944', strokeWidth: 0, r: 3 }}
          activeDot={{ r: 5, fill: '#00a944' }}
        />
        <Line
          type="monotone"
          dataKey="noMentions"
          name="No Mentions"
          stroke="#e14944"
          strokeWidth={2}
          dot={{ fill: '#e14944', strokeWidth: 0, r: 3 }}
          activeDot={{ r: 5, fill: '#e14944' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default MentionsChart;
